import _ from "lodash";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button } from "../../../components";
import { FormTextArea } from "../../../components/forms";
import { VirtualTable } from "../../../components/tables";
import { StandardTableSortableHeader, StickyThemedTableHeader } from "../../../components/tables/StandardTable";
import { TableRecordCountDisplayFooter } from "../../../components/tables/VirtualTable";
import ThemedModal from "../../../components/utilities/ThemedModal";
import usePortfolioTestResults from "../hooks/usePortfolioTestResults";
import PortfolioTestResultsTableRow from "./PortfolioTestResultsTableRow";

const PortfolioTestNotesModal = ({ masterAccount, onClose, onSave }) => {
    const { masterAccountId, masterAccountName, masterAccountFlagNotes } = masterAccount || {};
    const [notesText, setNotesText] = useState(masterAccountFlagNotes ?? "");

    const handleClose = () => {
        setNotesText(masterAccountFlagNotes);
        onClose();
    }

    const handleSave = () => {
        onSave(masterAccountId, notesText);
        onClose();
    }

    // Reset when master account changes (stops wrong account's notes showing up)
    useEffect(() => setNotesText(masterAccountFlagNotes), [masterAccountFlagNotes]);

    return <Modal show={masterAccountId != null} onHide={handleClose} centered size="lg">
        <ThemedModal.Header closeButton>
            <Modal.Title>
                <p className="m-0 lh-1 text-truncate">
                    <span>
                        {_.isEmpty(masterAccountFlagNotes) ? "Add" : "Edit"} Notes
                        <br />
                        <span className="fs-smaller">
                            {masterAccountName}
                        </span>
                    </span>
                </p>
            </Modal.Title>
        </ThemedModal.Header>
        <Modal.Body>
            <FormTextArea
                value={notesText}
                onChange={e => setNotesText(e.target.value)}
                disableAnimations
            />
        </Modal.Body>
        <Modal.Footer>
            <Button variant="danger" onClick={handleClose}>
                Cancel
            </Button>
            <Button variant="success" onClick={handleSave}>
                Save
            </Button>
        </Modal.Footer>
    </Modal>
}

const PortfolioTestResultsGrid = () => {
    const {
        clear,
        isClearing,
        isRowLoaded,
        loadMore,
        currentSort,
        splitSort: [sortProperty, sortDirection],
        onClickSort,
        totalCount,
        results,
        columns,
        error,
        noteModalMasterAccount,
        onNoteCancel,
        onNoteSave,
    } = usePortfolioTestResults();

    const footerSpan = Math.floor(columns.length / 2);
    return <div className="p-3">
        <PortfolioTestNotesModal
            masterAccount={noteModalMasterAccount}
            onClose={onNoteCancel}
            onSave={onNoteSave}
        />
        <VirtualTable
            enableScrollTracking
            row={(props) => <PortfolioTestResultsTableRow columns={columns} {...props} />}
            header={<thead>
                <tr>
                    {columns.map((col, index) =>
                        col.headerRenderer
                            ? col.headerRenderer({ index, ...col, currentSort, onClickSort })
                            : col.sort
                                ? <StandardTableSortableHeader
                                    key={index}
                                    active={sortProperty === col.sortProperty ?? col.property}
                                    direction={sortDirection}
                                    onClick={() => onClickSort(col.sortProperty ?? col.property)}
                                    isSticky
                                >
                                    {col.label}
                                </StandardTableSortableHeader>
                                : <StickyThemedTableHeader key={index} >
                                    {col.label}
                                </StickyThemedTableHeader>
                    )}
                </tr>
            </thead>}
            footer={<TableRecordCountDisplayFooter
                className="sticky-footer w-100"
                totalCount={totalCount}
                isNewRecordsDisplayed={false}
                newRecordsSinceCache={0}
                reload={clear}
                spanLeft={footerSpan}
                spanRight={columns.length - footerSpan}
            />}
            error={error}
            isLoading={isClearing}
            isItemLoaded={isRowLoaded}
            loadMoreItems={loadMore}
            itemSize={46}
            itemCount={totalCount === results.length ? totalCount : results.length + 3}
            itemData={results}
        />
    </div>
}

export default PortfolioTestResultsGrid;